import React, { useState, useEffect, useRef } from 'react';
import CanvasWorkspace from './CanvasWorkspace';
import { PAGE_CONFIGS } from './QuickInvoiceView';
import BilingualInvoiceRenderer from './BilingualInvoiceRenderer';
import POSReceiptRenderer from './POSReceiptRenderer';
import { getDefaultSchemeForCountryAndRegion, computeTaxBreakdown, buildCustomScheme } from '../utils/taxSchemes';
import { amountToWords } from '../utils/numberToWords';
const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

interface PublicInvoiceData {
  id: number;
  invoice_number: string;
  status: string;
  currency: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  client_address: string;
  amount: number;
  subtotal: number;
  vat_amount: number;
  total: number;
  notes: string;
  terms: string;
  elements: any[];
  page_size: string;
  issue_date: string;
  due_date: string;
  is_bilingual: boolean;
  seller_country?: string;
  seller_region?: string;
  buyer_region?: string;
  buyer_tax_id?: string;
  translated_lang?: string;
  row_labels_ar?: any;
  tax_lines_ar?: any;
  discount_lines_ar?: any;
  tax_breakdown_ar?: any;
  dynamic_values_ar?: any;
  dynamic_field_labels_ar?: any;
  amount_in_words_translated?: string;
  notes_translated?: string;
  show_amount_in_words?: boolean;
}

interface LineItem {
  id: string;
  description: string;
  qty: number;
  rate: number;
  arabicName?: string;
  taxRate?: number;
  taxInclusive?: boolean;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', INR: '₹', AED: 'د.إ',
  SAR: '﷼', AUD: '$', CAD: '$', JPY: '¥',
};

export default function PublicInvoiceView({ token }: { token: string }) {
  const [data, setData] = useState<PublicInvoiceData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [translatedValues, setTranslatedValues] = useState<Record<string, string>>({});
  const [taxBreakdownArState, setTaxBreakdownArState] = useState<Record<string, string>>({});
  const outerRef = useRef<HTMLDivElement>(null);
  const canvasWorkspaceRef = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(794);
  // Real rendered height of the invoice canvas, measured after paint —
  // replaces guessing at table/row height, which is fragile (wrapping,
  // font size, padding all change actual height unpredictably) and was
  // hard-clipping content below the table via the fixed-height wrapper.
  const [measuredContentHeight, setMeasuredContentHeight] = useState<number | null>(null);
  const [tableBottomOverride, setTableBottomOverride] = useState<number | null>(null);
  // If the real height measurement never lands within 1.5s, stop waiting
  // and show the invoice anyway using whatever estimate is available —
  // prevents a permanent blank screen when the measure ref never fires.
  const [forceShow, setForceShow] = useState(false);

  useEffect(() => {
    setForceShow(false);
    const t = setTimeout(() => setForceShow(true), 1500);
    return () => clearTimeout(t);
  }, [data]);

  const [qrImageMap, setQrImageMap] = useState<Record<string, string>>({});

  // The backend only ever stores a real qrValue (URL) on saved QR
  // elements, never a rendered image. Both POSReceiptRenderer's fallback
  // (generateQRMatrix) and CanvasWorkspace's fallback are decorative
  // approximations, not real encoders — they draw unscannable noise once
  // the encoded string is a real invoice URL rather than a short test
  // string. Generate a real, scannable PNG per QR element here so both
  // the bilingual and non-bilingual public-view branches get a working QR.
  useEffect(() => {
    if (!data?.elements) return;
    const qrEls = data.elements.filter((el: any) => el.type === 'qr' && el.qrValue);
    if (qrEls.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const QRCode = (await import('qrcode')).default;
        const entries = await Promise.all(
          qrEls.map(async (el: any) => {
            const url = await QRCode.toDataURL(el.qrValue, { width: 300, margin: 2 });
            return [el.id, url] as const;
          })
        );
        if (!cancelled) setQrImageMap(Object.fromEntries(entries));
      } catch { /* leave qrImageMap empty — falls back to decorative render */ }
    })();
    return () => { cancelled = true; };
  }, [data]);
useEffect(() => {
    if (!outerRef.current) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0].contentRect.width;
      if (w > 0) setMeasuredWidth(Math.floor(w));
    });
    ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, [data]);

  useEffect(() => {
    setMeasuredContentHeight(null); // force re-measure once the table push settles
  }, [tableBottomOverride]);

  // Auto-transliterate any custom tax & discount names (splitting numbers so rihan4 -> रिहान 4 and Loyal2 -> लोयल 2)
  useEffect(() => {
    if (!data?.elements) return;
    const isBilingual = Boolean(
      data.is_bilingual ||
      (data as any).isBilingual ||
      (data.elements || []).some((el: any) => Boolean(el.arabicContent || el.translatedLang))
    );
    const storedLang = (data as any).translated_lang || (data.elements || []).find((el: any) => el.translatedLang)?.translatedLang || 'en';
    if (!isBilingual || !storedLang || storedLang === 'en') return;

    const table = data.elements.find((el: any) => el.type === 'table');
    const breakdown = (table as any)?.taxBreakdown || (data as any)?.tax_breakdown || [];
    const discounts = (table as any)?.discounts || [];
    const savedAr = (data as any)?.tax_breakdown_ar || (data as any)?.dynamic_values_ar || {};
    
    const allNames = [
      ...breakdown.map((tb: any) => tb.name),
      ...discounts.map((d: any) => d.label),
    ].filter(Boolean);

    const missing = Array.from(new Set(allNames)).filter(
      (name: string) => name && !savedAr[name] && !taxBreakdownArState[name]
    );

    if (missing.length === 0) return;

    Promise.all(missing.map(async (name: string) => {
      try {
        const raw = name.trim();
        const baseWords = raw.replace(/\d+$/, '').trim();
        const trailingDigits = raw.match(/\d+$/)?.[0] || '';
        
        let tr = '';
        if (baseWords) {
          const res = await fetch(`${BACKEND}/transliterate?text=${encodeURIComponent(baseWords)}&tl=${storedLang}`);
          const json = await res.json();
          tr = json.transliterated || baseWords;
        }
        const finalTrans = trailingDigits ? `${tr || baseWords} ${trailingDigits}`.trim() : (tr || raw);
        return [name, finalTrans] as const;
      } catch {
        return [name, name] as const;
      }
    })).then(pairs => {
      const map = Object.fromEntries(pairs);
      setTaxBreakdownArState(prev => ({ ...prev, ...map }));
    });
  }, [data]);

  useEffect(() => {
    if (!token) {
      setError('Missing invoice link.');
      setLoading(false);
      return;
    }
    fetch(`${BACKEND}/invoices/public/${token}`)
      .then(async res => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Invoice not found.' : 'Failed to load invoice.');
        return res.json();
      })
      .then((d: PublicInvoiceData) => {
        console.log('🔍 [1. RAW BACKEND DATA]', {
          invoice_number: d.invoice_number,
          page_size: d.page_size,
          is_bilingual: d.is_bilingual,
          client_name: d.client_name,
          buyer_tax_id: d.buyer_tax_id,
          total_elements: d.elements?.length,
          all_text_elements: (d.elements || [])
            .filter((e: any) => e.type === 'text')
            .map((e: any) => ({
              id: e.id,
              text: e.textContent,
              arabic: e.arabicContent,
            })),
        });
        setData(d);
      })
      .catch(e => setError(e.message || 'Failed to load invoice.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f4f8' }}>
        <div style={{ width: 32, height: 32, border: '4px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }


  if (error || !data) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f4f8',
        gap: 8,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        <div style={{ fontSize: 40 }}>🔍</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{error || 'Invoice not found.'}</div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>This link may be invalid or the invoice was removed.</div>
      </div>
    );
  }

  // 1. Resolve table, Tax ID, and B2B status BEFORE constructing elements
  const tableEl = (data.elements || []).find((el: any) => el.type === 'table');
  const invNum = data.invoice_number || '';

  // ⚡ Check if the thermal card is already in the database elements
  const hasCustomerCardInDb = (data.elements || []).some((el: any) =>
    el.id === 'auto-injected-client-box' ||
    Number(el.borderLeftWidth) > 0 ||
    (el.borderLeft && String(el.borderLeft).includes('px'))
  );
  const targetLabelRegex = /^(bill\s*to|billed\s*to|invoice\s*to|client|customer|patient|sold\s*to|seller|buyer|to|recipient|email|phone|address|attn)\s*:/i;

  // Inspect the SAVED customer / seller text directly (even if Name is blank and box starts with Email/Phone)
  const originalBillToEl = (data.elements || []).find((el: any) =>
    el.type === 'text' && (
      targetLabelRegex.test((el.textContent || '').trim()) || 
      targetLabelRegex.test((el.textContent || '').split('\n')[0]) ||
      /^(email|phone|address|tax\s*id|gstin)\s*:/i.test((el.textContent || '').split('\n')[0].trim())
    )
  );

  const btLines = (originalBillToEl?.textContent || '').split('\n');
  const isNameLine = (line: string) => {
    const trimmed = (line || '').trim();
    if (!trimmed) return false;
    if (trimmed.includes('@')) return false;
    if (trimmed.includes(':')) return false;
    if (/^[\d\s+\-().]{6,}$/.test(trimmed)) return false;
    return true;
  };

  const hasNameInBlock = btLines.length > 1 && isNameLine(btLines[1]);

  // Check if cashier explicitly unticked any box
  const savedInclude = (data as any).include_fields || (data as any).includeFields || (data.elements?.[0] as any)?.include_fields || (data.elements?.[0] as any)?.includeFields;
  const allowTaxId = savedInclude ? savedInclude.taxId !== false : true;
  const allowName = savedInclude ? savedInclude.name !== false : true;
  const allowEmail = savedInclude ? savedInclude.email !== false : true;
  const allowPhone = savedInclude ? savedInclude.phone !== false : true;
  const allowAddress = savedInclude ? savedInclude.address !== false : true;

  const rawExtractedTaxId = btLines.find((l: string) => /^(tax\s*id|vat\s*no|gstin|trn|npwp)\s*:/i.test(l.trim()))?.replace(/^[^:]+:\s*/, '').trim() || '';
  const rawExtractedAddress = btLines.find(l => /^(address|addr)\s*:/i.test(l.trim()))?.replace(/^[^:]+:\s*/, '').trim() || '';

  // Checks database column -> dynamic_values_ar -> table element -> text block
  const buyerTaxId = allowTaxId ? (
    data.buyer_tax_id ||
    (data as any).buyerTaxId ||
    (data.dynamic_values_ar || {})?.buyer_tax_id ||
    (data as any).dynamicValuesAr?.buyer_tax_id ||
    (tableEl as any)?.buyer_tax_id ||
    (tableEl as any)?.buyerTaxId ||
    (data.elements?.[0] as any)?.buyer_tax_id ||
    (data.elements?.[0] as any)?.buyerTaxId ||
    rawExtractedTaxId ||
    ''
  ) : '';

  const clientName = allowName ? (data.client_name || (hasNameInBlock ? btLines[1] : '') || '') : '';
  const clientEmail = allowEmail ? (data.client_email || btLines.find((l: string) => l.includes('@')) || '') : '';
  const clientPhone = allowPhone ? (data.client_phone || btLines.find((l: string) => /^[\d\s+\-().]{6,}$/.test(l.trim()) && !l.includes('@')) || '') : '';
  const clientAddress = allowAddress ? (data.client_address || (data as any).clientAddress || rawExtractedAddress || '') : '';
  const isB2B = Boolean(buyerTaxId && buyerTaxId.length > 0);

  let billToGrowthDelta = 0;
  let billToOriginalBottom = 0;

  if (originalBillToEl) {
    const lineCount = (originalBillToEl.textContent || '').split('\n').length;
    const calculatedHeight = Math.max(originalBillToEl.height || 40, lineCount * 18 + 14);
    billToGrowthDelta = Math.max(0, calculatedHeight - (originalBillToEl.height || 40));
    billToOriginalBottom = (originalBillToEl.y || 0) + (originalBillToEl.height || 40);
  }

  const filteredElements = (data.elements || [])
    .filter((el: any) => {
      // ⚡ DROP the garbled squeezed database card so only the clean card renders!
      if (el.id === 'auto-injected-client-box') {
        return false;
      }
      if (el.type === 'text') {
        const t = (el.textContent || '').trim().toLowerCase();
        if (el.id === 'pdf-amount-in-words' || el.id === 'preview-amount-in-words' || t.startsWith('amount in words:')) {
          return false;
        }
      }
      return true;
    })
    .map((el: any) => {
      // 1. Auto-expand background shape/card behind Bill To
      if ((el.type === 'shape' || el.type === 'rectangle' || el.type === 'container') && originalBillToEl) {
        const isBehindBillTo = Math.abs((el.x || 0) - (originalBillToEl.x || 0)) < 80 && Math.abs((el.y || 0) - (originalBillToEl.y || 0)) < 60;
        if (isBehindBillTo) {
          return { ...el, height: (el.height || 40) + billToGrowthDelta };
        }
      }

      // 2. Shift the table and elements below Bill To down so nothing collides
      const isBelowBillTo = originalBillToEl && (el.y || 0) >= billToOriginalBottom - 15 && el.id !== originalBillToEl.id;
      const adjustedY = isBelowBillTo ? (el.y || 0) + billToGrowthDelta : (el.y || 0);

      // 3. Ensure all invoice QR codes point to the live public link
      if (el.type === 'qr') {
        const realQrValue = `${window.location.origin}/invoice/view/${token}`;
        return {
          ...el,
          y: adjustedY,
          qrValue: realQrValue,
          imageDataUrl: qrImageMap[el.id] || el.imageDataUrl,
        };
      }

      // 4. Preserve custom stamp (e.g. FEES SETTLED) exactly as saved
      if (el.type === 'stamp') {
        return {
          ...el,
          y: adjustedY,
        };
      }

      if (el.type !== 'text') return { ...el, y: adjustedY };

      let text = (el.textContent || '').replace(/\{invoice_number\}/g, invNum);

      if (text.toLowerCase().includes('scan to pay net30') || el._auto === 'invoice_label' || el.id?.includes('invoice-label')) {
        text = 'Scan to view invoice';
      }

      // Use the actual saved textContent directly without forcing unselected email/phone
      if (originalBillToEl && el.id === originalBillToEl.id) {
        const lineCount = (el.textContent || '').split('\n').length;
        return {
          ...el,
          textContent: text,
          arabicContent: el.arabicContent,
          height: Math.max(el.height || 40, lineCount * 18 + 14),
        };
      }

      return {
        ...el,
        y: adjustedY,
        textContent: text,
        arabicContent: el.arabicContent
          ? (() => {
              let ar = el.arabicContent.replace(/\{invoice_number\}/g, invNum);
              if (invNum && text.includes(invNum)) {
                ar = ar.replace(/[^\s:\d٠-٩]+-[٠-٩\d]{3,}/g, `\u2066${invNum}\u2069`);
              }
              return ar;
            })()
          : el.arabicContent,
      };
    });

  // BilingualInvoiceRenderer receives filteredElements TWICE below (as
  // template.elements AND as savedElements) — savedElements is what it
  // uses to auto-inject the real QR/label pair. If the _auto QR/label
  // elements are also left inside template.elements, they render once
  // as ordinary elements AND once again via the auto-injection, producing
  // two identical "Scan to view invoice" QR blocks. Strip them from the
  // template copy so savedElements is the only source for the real QR.
  const templateElements = filteredElements.filter((el: any) =>
    !el.id?.startsWith('invoice-qr-') &&
    !el.id?.startsWith('invoice-label-') &&
    !el.id?.startsWith('token-qr-') &&
    !el.id?.startsWith('token-label-')
  );
  const template = {
    id: String(data.id),
    name: data.invoice_number,
    elements: templateElements,
    pageSize: data.page_size || 'A4',
    category: 'Custom',
    description: '',
    preview: '',
  } as any;

  // Extract line items and auto-detect bilingual from boolean or element contents
  const isBilingual = Boolean(
    data.is_bilingual ||
    (data as any).isBilingual ||
    data.translated_lang ||
    (data as any).translatedLang ||
    (data as any).dynamic_values_ar && Object.keys((data as any).dynamic_values_ar).length > 0 ||
    (data.elements || []).some((el: any) => Boolean(el.arabicContent || el.translatedLang || el.columns?.some((c: any) => c.arabicHeader || c.translatedLang) || el.rows?.some((r: any) => r.values?.arabicDescription)))
  );
  const hasTrackingQR = (data.elements || []).some((el: any) => el.id?.startsWith('token-qr-'));
  const storedLang = (data as any).translated_lang || (data.elements || []).find((el: any) => el.translatedLang)?.translatedLang || 'en';

  const lineItems: LineItem[] = (tableEl?.rows || []).map((r: any, i: number) => {
    const rawB2b = r.values?.b2bTaxRate ?? r.values?.b2b_tax_rate;
    const hasB2b = isB2B && rawB2b !== undefined && rawB2b !== null && String(rawB2b).trim() !== '';
    const rate = hasB2b
      ? (parseFloat(String(rawB2b)) || 0)
      : (parseFloat(String(r.values?.taxRate ?? r.values?.tax_rate ?? 0)) || 0);

    const rawB2bName = r.values?.b2bTaxName ?? r.values?.b2b_tax_name;
    const name = (isB2B && rawB2bName)
      ? String(rawB2bName).trim()
      : String(r.values?.taxName || r.values?.tax_name || 'Tax').trim();

    return {
      id: r.id || `li-${i}`,
      description: r.values?.description || '',
      qty: parseFloat(String(r.values?.qty)) || 0,
      rate: parseFloat(String(r.values?.rate)) || 0,
      arabicName: r.values?.arabicName || r.values?.arabicDescription || '',
      taxRate: rate,
      taxName: name,
      taxInclusive: !!(r.values?.taxInclusive ?? r.values?.tax_inclusive),
      ...r.values,
      customColAr: r.values?.__customAr,
    };
  });

  // Tax/discount lines — synchronously mapped with saved translations & auto-transliterations
  const dynAr = (data as any).dynamic_values_ar || (data as any).dynamicValuesAr || {};
  const discountsArList = (tableEl as any)?.discountsAr || (data as any).discount_lines_ar || [];
  const taxesArList = (tableEl as any)?.taxesAr || [];

  const rawTaxLines = (tableEl?.taxes && tableEl.taxes.length > 0) ? tableEl.taxes : [];
  const taxLines = rawTaxLines.map((t: any, i: number) => ({
    ...t,
    translatedLabel: t.translatedLabel || t.labelAr || taxesArList[i]?.labelAr || taxesArList[i] || dynAr[`tax_${i}`] || taxBreakdownArState[t.label] || undefined
  }));

  const allDiscounts = (tableEl?.discounts && tableEl.discounts.length > 0) ? tableEl.discounts : [];
  const dlAr: any[] = (data as any).discount_lines_ar || [];
  const arLabelFor = (label: string) => dlAr.find((x: any) => x.label === label)?.labelAr;
  const loyaltyLine = allDiscounts.find((d: any) => d._isAuto || /loyalty/i.test(d.label || ''));
  const loyaltyLabelAr = loyaltyLine ? (arLabelFor(loyaltyLine.label) || dynAr['label_loyalty'] || taxBreakdownArState[loyaltyLine.label]) : undefined;
  
  const discountLines = allDiscounts
    .filter((d: any) => d !== loyaltyLine)
    .map((d: any, i: number) => {
      const savedTrans = d.translatedLabel || d.labelAr || discountsArList[i]?.labelAr || discountsArList[i] || dynAr[`discount_${i}`] || taxBreakdownArState[d.label] || arLabelFor(d.label);
      return { ...d, translatedLabel: savedTrans, labelAr: savedTrans };
    });
  const shippingCost = tableEl?.shippingCost || 0;

  // Compute subtotal and base directly from the rows
  const lineBases = lineItems.map((li: any) => {
    const lineTotal = li.qty * li.rate;
    const taxRate = li.taxRate || 0;
    const inclusive = !!li.taxInclusive;
    const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
    return { base, taxRate };
  });
  const netSubtotal = lineBases.reduce((s, lb) => s + lb.base, 0);
  const autoDiscountAmount = loyaltyLine
    ? (loyaltyLine.amount ?? (netSubtotal * (loyaltyLine.rate / 100)))
    : 0;

  // Calculate rate and attach (-X%) to both English and Hindi labels
  const loyaltyRate = loyaltyLine?.rate != null && Number(loyaltyLine.rate) > 0
    ? Math.abs(parseFloat(Number(loyaltyLine.rate).toFixed(2)))
    : (netSubtotal > 0 && autoDiscountAmount > 0
        ? Math.abs(parseFloat(((autoDiscountAmount / netSubtotal) * 100).toFixed(2)))
        : 0);

  const rateSuffix = loyaltyRate > 0 ? ` (-${loyaltyRate}%)` : '';

  const rawLoyaltyLabel = loyaltyLine?.label || 'Loyalty Discount';
  const loyaltyLabelWithRate = rawLoyaltyLabel.includes('%')
    ? rawLoyaltyLabel
    : `${rawLoyaltyLabel}${rateSuffix}`;

  const rawLoyaltyLabelAr = loyaltyLabelAr || dynAr['label_loyalty'] || taxBreakdownArState[rawLoyaltyLabel] || rawLoyaltyLabel;
  const loyaltyLabelArWithRate = rawLoyaltyLabelAr
    ? (rawLoyaltyLabelAr.includes('%') ? rawLoyaltyLabelAr : `${rawLoyaltyLabelAr}${rateSuffix}`)
    : loyaltyLabelWithRate;
  const discTotalForTax = discountLines.reduce((s: number, d: any) => s + netSubtotal * (d.rate / 100), 0) + autoDiscountAmount;
  const discountRatio = netSubtotal > 0 ? Math.min(1, discTotalForTax / netSubtotal) : 0;
  const productTaxTotal = lineBases.reduce(
    (s, lb) => s + (lb.base * (1 - discountRatio)) * (lb.taxRate / 100), 0
  );

  const savedTaxBreakdown: { name: string; rate: number; amount: number }[] | undefined = (() => {
    if (productTaxTotal <= 0) return undefined;

    const directBreakdown = (tableEl as any)?.taxBreakdown || (data as any)?.tax_breakdown || (data as any)?.taxBreakdown;
    const combined: Record<string, { rate: number; amount: number }> = {};

    lineItems.forEach((li, i) => {
      if (li.taxRate <= 0) return;
      const discountedBase = lineBases[i].base * (1 - discountRatio);

      // Check the row's B2B name -> row's tax name -> table's saved breakdown name -> fallback 'Tax'
      const rawName = (isB2B && ((li as any).b2bTaxName || (li as any).b2b_tax_name)) ||
                      li.taxName || (li as any).tax_name ||
                      directBreakdown?.[i]?.name || directBreakdown?.[0]?.name ||
                      'Tax';
      const name = String(rawName).replace(/\s*\(.*?\)/g, '').trim() || 'Tax';

      if (!combined[name]) combined[name] = { rate: li.taxRate, amount: 0 };
      combined[name].amount += (discountedBase * li.taxRate / 100);
    });

    const entries = Object.entries(combined).map(([name, v]) => ({ name, rate: v.rate, amount: v.amount }));
    return entries.length > 0 ? entries : undefined;
  })();

  const publicTaxScheme = getDefaultSchemeForCountryAndRegion(data.seller_country || '', data.seller_region || '');

  // Read saved transliterations and combine with freshly resolved ones
  const savedTaxBreakdownAr: Record<string, string> =
    (data as any)?.tax_breakdown_ar ||
    (data as any)?.dynamic_values_ar ||
    {};

  const activeTaxBreakdownAr = {
    ...savedTaxBreakdownAr,
    ...taxBreakdownArState,
  };

  const saved: any = data;
  if (isBilingual && typeof window !== 'undefined') {
    const w = window as any;
    w.__pos_row_labels_ar = saved.row_labels_ar || undefined;
    w.__pos_tax_lines_ar = saved.tax_lines_ar || undefined;
    w.__pos_discount_lines_ar = saved.discount_lines_ar || undefined;
    w.__pos_tax_breakdown_ar = activeTaxBreakdownAr;
    w.__pos_amount_words_ar = saved.amount_in_words_translated || undefined;
    w.__pos_notes_ar = saved.notes_translated || undefined;
    w.__pos_client_name_ar = saved.dynamic_values_ar?.clientName || undefined;
    const fl = saved.dynamic_field_labels_ar || {};
    w.__pos_buyer_tax_label = fl.tax_id || undefined;
    w.__pos_email_label = fl.email || undefined;
    w.__pos_phone_label = fl.phone || undefined;
    w.__pos_address_label = fl.address || undefined;
    w.__pos_qr_invoice_label = saved.dynamic_values_ar?.qr_caption_invoice || undefined;
    w.__pos_qr_token_label = saved.dynamic_values_ar?.qr_caption_tracking || undefined;
  }
  const issueTime = (() => {
    if (!data.issue_date) return '';
    const raw = String(data.issue_date);
    const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(raw) ? raw : raw + 'Z');
    return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  })();
  const sym = CURRENCY_SYMBOLS[data.currency] || data.currency || '$';

  const issueDateObj = (() => {
    if (!data.issue_date) return null;
    const raw = String(data.issue_date);
    const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(raw) ? raw : raw + 'Z');
    return isNaN(d.getTime()) ? null : d;
  })();
  const issueDate = issueDateObj
    ? `${issueDateObj.getFullYear()}-${String(issueDateObj.getMonth() + 1).padStart(2, '0')}-${String(issueDateObj.getDate()).padStart(2, '0')}`
    : '';
  const dueDate = data.due_date ? new Date(data.due_date).toISOString().split('T')[0] : '';

  // Detect whether user placed the QR on Left, Right, or Center from saved X coordinate
  const publicQrPos: 'left' | 'center' | 'right' = (() => {
    if ((data as any).qr_position) return (data as any).qr_position;
    if ((data as any).autoQrPosition) return (data as any).autoQrPosition;

    const invQr = (data.elements || []).find((el: any) =>
      el._auto === 'invoice' || el.id?.startsWith('invoice-qr-') || el.id?.startsWith('auto-invoice-qr-')
    );
    if (invQr && typeof invQr.x === 'number') {
      if (invQr.x <= 180) return 'left';
      if (invQr.x >= 480) return 'right';
      return 'center';
    }
    return 'center';
  })();

  return (
    <div id="public-invoice-wrap" style={{
      minHeight: '100vh',
      width: '100%',
      background: '#f0f4f8',
      padding: '32px 16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      boxSizing: 'border-box',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <div style={{ marginBottom: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{data.invoice_number}</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Shared invoice — view only</div>
      </div>
      <style>{`
        /* CanvasWorkspace renders its own internal designer chrome as
           direct children of <main> — meant for the editor, not a
           read-only public link. Hide all three pieces here:
           1) the toolbar (Single/Auto/A4 Split, zoom slider)
           2) the dotted-grid "virtual desktop" background behind the page
           3) the "Interactive Vector Editor" drag/resize tooltip */
        .public-invoice-cw-embed main > style + div { display: none !important; }
        .public-invoice-cw-embed main > div[class*="overflow-auto"] {
          background-image: none !important;
          padding: 0 !important;
        }
        .public-invoice-cw-embed main > div[class*="bottom-5"] { display: none !important; }
        /* Page-break warning line (amber/rose dashed line + "Page N ends
           here" label) — an editor-only heads-up that a page break will
           occur, not something a client viewing their invoice needs. */
        .public-invoice-cw-embed [class*="border-amber-400"],
        .public-invoice-cw-embed [class*="border-rose-450"] { display: none !important; }
      `}</style>
      <div ref={outerRef} className="public-invoice-cw-embed" style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', background: '#fff', width: (data.page_size === 'THERMAL_80MM' || data.page_size === 'THERMAL_58MM') ? 'auto' : '100%', maxWidth: data.page_size === 'THERMAL_80MM' ? 302 : data.page_size === 'THERMAL_58MM' ? 220 : 794, marginLeft: 'auto', marginRight: 'auto', textAlign: (data.page_size === 'THERMAL_80MM' || data.page_size === 'THERMAL_58MM') ? 'center' : 'left' }}>
     {(() => {
       const isThermalBranch = data.page_size === 'THERMAL_80MM' || data.page_size === 'THERMAL_58MM';
       console.log('🔍 [2. ACTIVE RENDERER BRANCH]', {
         branch: isThermalBranch ? 'POSReceiptRenderer (Thermal)' : isBilingual ? 'BilingualInvoiceRenderer' : 'CanvasWorkspace',
         page_size: data.page_size,
         isBilingual,
         hasCustomerCardInDb,
         elements_being_rendered: filteredElements
           .filter((e: any) => e.type === 'text')
           .map((e: any) => ({
             id: e.id,
             text: (e.textContent || '').replace(/\n/g, ' ↵ '),
             arabic: (e.arabicContent || '').replace(/\n/g, ' ↵ '),
           })),
       });
       return null;
     })()}

     {(data.page_size === 'THERMAL_80MM' || data.page_size === 'THERMAL_58MM') ? (
  <POSReceiptRenderer
    containerWidthProp={measuredWidth}
    template={{ elements: filteredElements, pageSize: data.page_size }}
    clientName={data.client_name || ''}
    clientEmail={data.client_email || ''}
    clientPhone={data.client_phone || ''}
    clientAddress={data.client_address || ''}
    buyerTaxId={buyerTaxId || undefined}
    isB2B={isB2B}
    invoiceDate={issueDate}
    invoiceTime=""
    notes={data.notes || ''}
    lineItems={lineItems}
    taxLines={taxLines}
    discountLines={discountLines}
    sym={sym}
    shippingCost={shippingCost}
    invoiceStatus={data.status}
    translateTargetLang={storedLang}
    isBilingual={isBilingual}
    isThermal={true}
    showTokenQRPlaceholder={false}
    showWalkIn={true}
    productTaxTotal={productTaxTotal}
    netSubtotal={netSubtotal}
    autoDiscountAmount={autoDiscountAmount}
    autoDiscountLabel={loyaltyLabelWithRate}
    dynamicValuesAr={{
      ...((data as any).dynamic_values_ar || {}),
      label_loyalty: loyaltyLabelArWithRate,
    }}
    clientNameAr={(data as any).dynamic_values_ar?.clientName}
    dynamicFieldLabelsAr={(data as any).dynamic_field_labels_ar}
    rowLabelsAr={(data as any).row_labels_ar}
    taxLinesAr={(data as any).tax_lines_ar}
    discountLinesAr={(data as any).discount_lines_ar}
    notesAr={(data as any).notes_translated}
    taxBreakdown={savedTaxBreakdown?.map(tb => ({
      ...tb,
      nameAr: (tb as any).nameAr || activeTaxBreakdownAr[tb.name] || tb.name,
    }))}
    taxBreakdownAr={activeTaxBreakdownAr}
    showAmountInWords={Boolean((data as any).show_amount_in_words ?? true)}
    amountInWords={amountToWords(data.amount || (netSubtotal - discTotalForTax + productTaxTotal + shippingCost), data.currency || 'INR')}
    amountInWordsTranslated={(data as any).amount_in_words_translated || (data as any).amount_in_words_ar || ''}
  />
) : isBilingual ? (() => {
  // BilingualInvoiceRenderer always renders at a fixed native width
  // (794/816/302px depending on page_size) — it has no responsive
  // scaling of its own. The plain CanvasWorkspace branch below solves
  // this by measuring real content height and scaling to fit the
  // viewport; this branch had neither, so on a phone-width screen the
  // fixed-width content just got clipped by the outer overflow:hidden
  // instead of shrinking — the cut-off invoice seen on mobile.
  const bilingualPageWidth =
    data.page_size === 'THERMAL_80MM' ? 302 :
    data.page_size === 'THERMAL_58MM' ? 220 :
    data.page_size === 'LETTER' ? 816 : 794;
  const bilingualFitScale = Math.min(1, measuredWidth / bilingualPageWidth);
  const realHeight = measuredContentHeight ?? bilingualPageWidth * 1.4; // rough guess before first measurement
  const heightIsFinal = measuredContentHeight !== null || forceShow;

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      height: realHeight * bilingualFitScale,
      opacity: heightIsFinal ? 1 : 0,
      transition: 'opacity 0.15s ease',
    }}>
      <div
        ref={(node: HTMLDivElement | null) => {
          if (!node) return;
          // BilingualInvoiceRenderer's actual content div carries
          // data-bilingual-invoice-page="true" — measure THAT, not this
          // wrapper (which has no intrinsic height of its own).
          const pageNode = node.querySelector('[data-bilingual-invoice-page="true"]') as HTMLElement | null;
          const h = pageNode?.scrollHeight;
          if (h && h > 0 && h !== measuredContentHeight) {
            requestAnimationFrame(() => setMeasuredContentHeight(h));
          }
        }}
        style={{
          width: bilingualPageWidth,
          transform: `scale(${bilingualFitScale})`,
          transformOrigin: 'top left',
        }}
      >
        <BilingualInvoiceRenderer
          template={template}
          clientName={clientName}
          clientEmail={clientEmail}
          clientPhone={clientPhone}
          clientAddress={clientAddress}
          buyerTaxId={buyerTaxId || undefined}
          isB2B={isB2B}
          taxBreakdown={savedTaxBreakdown}
          taxBreakdownAr={activeTaxBreakdownAr}
          autoQrPosition={publicQrPos}
          autoDiscountAmount={autoDiscountAmount}
          autoDiscountLabel={loyaltyLabelWithRate}
          autoDiscountLabelAr={loyaltyLabelArWithRate}
          dynamicValuesAr={{
            ...((data as any).dynamic_values_ar || {}),
            label_loyalty: loyaltyLabelArWithRate,
          }}
          totalsLabels={(() => {
            const r = (data as any).row_labels_ar || {};
            const d = (data as any).dynamic_values_ar || {};
            return {
              subtotal: r.subtotal || d.label_subtotal || '',
              total: r.total || d.label_total || '',
              tax: r.tax || d.label_tax || '',
              discount: r.discount || d.label_discount || '',
              shipping: r.shipping || d.label_shipping || '',
            };
          })()}
          invoiceDate={issueDate}
          invoiceTime=""
          notes={data.notes || ''}
          lineItems={lineItems}
          taxLines={taxLines}
          discountLines={discountLines}
          sym={sym}
          shippingCost={shippingCost}
          invoiceStatus={data.status}
          translateTargetLang={storedLang}
          isBilingual={true}
          bilingualLayout="side"
          isThermal={data.page_size === 'THERMAL_80MM'}
          savedElements={filteredElements}
          showTrackingQR={hasTrackingQR}
          invoiceNumber={data.invoice_number}
          showAmountInWords={Boolean((data as any).show_amount_in_words)}
          amountInWordsTranslated={(data as any).amount_in_words_translated || ''}
          dynamicFieldLabelsAr={(data as any).dynamic_field_labels_ar}
          notesTranslated={(data as any).notes_translated || ''}
          taxScheme={publicTaxScheme}
          sellerRegion={data.seller_region}
          buyerRegion={data.buyer_region}
        />
      </div>
    </div>
  );
})() : (() => {
  // Saved invoices already have final values baked into filteredElements
  // (table rows, taxes, discounts, patched text) — no need to rebuild from
  // form state like the live editor does. Just hand them to CanvasWorkspace
  // as-is for a static, view-only render.
  const pc = PAGE_CONFIGS[data.page_size || 'A4'] || PAGE_CONFIGS['A4'];
  // The table's stored `height` is often stale relative to its actual row
  // count (CanvasWorkspace renders table rows with overflow:visible, so
  // it can grow far taller on screen than the saved height property).
  // Using the stale height here undersizes the outer paper container,
  // cutting off content (totals, QR, notes) below the table well before
  // the table itself visually finishes — even though nothing is actually
  // clipped inside CanvasWorkspace. Estimate the table's real height from
  // its row count instead of trusting the stored value.
  const estimateElBottom = (el: any) => {
    if (el.type !== 'table') return (el.y || 0) + (el.height || 0);
    const rowCount = el.rows?.length || 0;
    const taxCount = (el.taxes || []).filter((t: any) => t.rate > 0).length;
    const discCount = (el.discounts || []).filter((d: any) => d.rate > 0).length;
    const totalsBoxHeight = 28 + (taxCount + discCount) * 18 + (el.shippingCost ? 18 : 0);
    const estimatedHeight = 40 + Math.max(rowCount, 1) * 20 + totalsBoxHeight;
    return (el.y || 0) + Math.max(el.height || 0, estimatedHeight);
  };
  const contentBottom = filteredElements.reduce(
    (max: number, el: any) => Math.max(max, estimateElBottom(el)),
    0
  ) + 60;
  const computedHeight = Math.max(pc.height, contentBottom);

  // The estimate above is still a guess — it can undershoot the table's
  // REAL rendered height for very long tables (60+ rows), leaving the QR
  // and other elements below the table sitting at a y that lands inside
  // the table's actual visual bounds instead of below it. Measure the
  // real table DOM height after paint and push every element below the
  // table down by the shortfall, same fix already applied in App.tsx's
  // PDF export (handleExportPDF) but never ported to this public view.
  const measureTableRef = (node: HTMLElement | null) => {
    if (!node) return;
    const tableEl = filteredElements.find((e: any) => e.type === 'table');
    if (!tableEl) return;
    // getBoundingClientRect() returns the ON-SCREEN height, already
    // shrunk by the `transform: scale(fitScale)` wrapper around this
    // canvas. el.y for every element lives in the UNSCALED coordinate
    // space CanvasWorkspace lays elements out in — mixing a scaled
    // screen height with unscaled y math silently undersizes pushDelta
    // (it happens to roughly cancel out the estimate's own undercount,
    // landing near zero), which is why the QR stayed frozen inside the
    // table instead of moving below it. Divide back out to unscaled px.
    // Measure where the visible content ends, not the wrapper box (which can be taller than its content)
    const nodeRect = node.getBoundingClientRect();
    let maxBottom = nodeRect.top;
    node.querySelectorAll('*').forEach((c) => {
      const r = (c as HTMLElement).getBoundingClientRect();
      if (r.height > 0 && r.bottom > maxBottom) maxBottom = r.bottom;
    });
    const rawHeight = (maxBottom - nodeRect.top) / (fitScale || 1);
    const realBottom = (tableEl.y || 0) + rawHeight;
    if (Math.abs(realBottom - (tableBottomOverride ?? 0)) > 2) {
      requestAnimationFrame(() => setTableBottomOverride(realBottom));
    }
  };

  const estimatedTableEl = filteredElements.find((e: any) => e.type === 'table');
  const estimatedTableTop = estimatedTableEl ? (estimatedTableEl.y || 0) : 0;
  const estimatedTableBottom = estimatedTableEl
    ? (estimatedTableEl.y || 0) + (estimatedTableEl.height || 0)
    : 0;
  const pushDelta = tableBottomOverride !== null
    ? Math.max(0, tableBottomOverride - estimatedTableBottom)
    : 0;
  // Compare against the table's TOP, not the estimated bottom. The
  // estimate (rowCount * 20px) undercounts real row height for tables
  // with many rows — a QR/element whose saved y sits well ABOVE that
  // undercounted estimated bottom (but still below the table's actual
  // top) was being skipped by the old ">= estimatedTableBottom - 10"
  // check, so it never got pushed and rendered at a stale y that landed
  // visually inside the real, taller table instead of below it.
  const pushedElementsRaw = pushDelta > 0
    ? filteredElements.map((el: any) =>
        el.type !== 'table' && (el.y || 0) > estimatedTableTop
          ? { ...el, y: (el.y || 0) + pushDelta }
          : el
      )
    : filteredElements;

  const MIN_GAP_AFTER_TABLE = 16;
  // Auto invoice/token QR row: keep its saved layout, only move it as one block if needed
  const isAutoRowEl = (el: any) =>
    el.id?.startsWith('invoice-qr-') || el.id?.startsWith('invoice-label-') ||
    el.id?.startsWith('token-qr-') || el.id?.startsWith('token-label-');
  const realTableBottom = tableBottomOverride ?? estimatedTableBottom;
  const belowTable = pushedElementsRaw
    .filter((el: any) => el.type !== 'table' && !isAutoRowEl(el) && (el.y || 0) > estimatedTableTop)
    .sort((a: any, b: any) => (a.y || 0) - (b.y || 0));

  // Match each QR code to its caption text below it so they stay locked as one unit
  const qrCaptions = new Map<any, any>(); // caption element -> parent QR element
  belowTable.forEach((el: any) => {
    if (el.type === 'qr') {
      const qrBottom = (el.y || 0) + (el.height || 0);
      const cap = belowTable.find((other: any) =>
        other.type === 'text' &&
        (other.y || 0) >= qrBottom - 4 &&
        (other.y || 0) <= qrBottom + 30 &&
        Math.abs((other.x || 0) - (el.x || 0)) < (el.width || 0) + 60
      );
      if (cap) qrCaptions.set(cap, el);
    }
  });

  let cursor = realTableBottom + MIN_GAP_AFTER_TABLE;
  const clampedY = new Map<any, number>();

  belowTable.forEach((el: any) => {
    if (qrCaptions.has(el)) {
      // Caption: place exactly 3px directly below its parent QR code
      const parentQr = qrCaptions.get(el);
      const qrY = clampedY.get(parentQr) ?? (parentQr.y || 0);
      const targetY = qrY + (parentQr.height || 0) + 3;
      clampedY.set(el, targetY);
      cursor = Math.max(cursor, targetY + (el.height || 16) + MIN_GAP_AFTER_TABLE);
      return;
    }

    const elY = el.y || 0;
    const elHeight = el.height || 0;
    if (elY < cursor) {
      clampedY.set(el, cursor);
      cursor += elHeight + (el.type === 'qr' ? 3 : MIN_GAP_AFTER_TABLE);
    } else {
      clampedY.set(el, elY);
      cursor = Math.max(cursor, elY + elHeight + (el.type === 'qr' ? 3 : MIN_GAP_AFTER_TABLE));
    }
  });

  const autoRowEls = pushedElementsRaw.filter(isAutoRowEl);
  const autoRowTop = autoRowEls.length ? Math.min(...autoRowEls.map((e: any) => e.y || 0)) : 0;
  // cursor is now below everything else (table, notes...), so the QR row only shifts if it would overlap
  const autoRowShift = autoRowEls.length ? Math.max(0, cursor - autoRowTop) : 0;
  const pushedElements = pushedElementsRaw.map((el: any) =>
    isAutoRowEl(el) ? (() => {
      const isQr = el.id?.startsWith('invoice-qr-') || el.id?.startsWith('token-qr-');
      const key = el.id?.startsWith('invoice-') ? 'invoice-' : 'token-';
      const qr = autoRowEls.find((e: any) => e.id?.startsWith(key + 'qr-'));
      const label = autoRowEls.find((e: any) => e.id?.startsWith(key + 'label-'));
      let y = (el.y || 0) + autoRowShift;
      if (qr && label && (label.y || 0) < (qr.y || 0)) {
        // old layout (label above QR): QR takes the label's spot, label goes right under it
        y = (isQr ? (label.y || 0) : (label.y || 0) + (qr.height || 80) + 4) + autoRowShift;
      }
      return { ...el, y };
    })()
    : clampedY.has(el) ? { ...el, y: clampedY.get(el) } : el
  );

  // computedHeight + pushDelta only accounts for the table's own
  // overshoot — it never checks where the cascade of clamped elements
  // below the table (QR, caption, notes...) actually landed. When that
  // cascade pushes further than the table overshoot alone would, the
  // white paper background (sized off computedHeight) ends before the
  // real content does, leaving the tail end of the invoice rendered on
  // the page's background instead of the paper's white one.
  const finalContentBottom = pushedElements.reduce(
    (max: number, el: any) => Math.max(max, (el.y || 0) + (el.height || 0)),
    0
  ) + 60;
  const finalComputedHeight = Math.max(computedHeight + pushDelta, finalContentBottom);

  // Fit the fixed-pixel-width canvas (e.g. 794px for A4) to the actual
  // measured container width — otherwise on mobile, where the viewport is
  // narrower than the page, the canvas overflows and gets cropped by the
  // wrapper's overflow:hidden instead of shrinking to fit like the rest
  // of a responsive page would.
  const fitScale = Math.min(1, measuredWidth / pc.width);
  const realHeight = measuredContentHeight ?? Math.max(finalComputedHeight, contentBottom);
  const heightIsFinal = measuredContentHeight !== null || forceShow;

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      height: realHeight * fitScale,
      opacity: heightIsFinal ? 1 : 0,
      transition: 'opacity 0.15s ease',
    }}>
    <div
      ref={(node: HTMLDivElement | null) => {
        // Measure the real rendered height once content has painted —
        // fixes the guessed computedHeight cutting off totals/QR/notes
        // below tables with many rows. Runs on every render; cheap and
        // idempotent since it only calls setState when the value changes.
        if (node) {
          const h = node.scrollHeight;
          if (h > 0 && h !== measuredContentHeight) {
            requestAnimationFrame(() => setMeasuredContentHeight(h));
          }
        }
      }}
      style={{
        width: pc.width,
        maxWidth: pc.width,
        overflow: 'hidden',
        transform: `scale(${fitScale})`,
        transformOrigin: 'top left',
      }}
    >
    <CanvasWorkspace
      pageConfig={{ ...pc, name: data.page_size || 'A4' }}
      computedHeight={finalComputedHeight}
      pageCount={1}
      pageBehavior="auto_expand"
      setPageBehavior={() => {}}
      zoom={1}
      setZoom={() => {}}
      showGridLines={false}
      elements={pushedElements}
      selectedElementId={null}
      inlineEditingId={null}
      inlineEditText=""
      setInlineEditText={() => {}}
      lockedElementIds={[]}
      dragState={null}
      qrDataUrls={qrImageMap}     
       isDrawingMode={false}
      isDrawingActive={false}
      currentPathPoints={[]}
      currentPenColor="#000000"
      currentPenWidth={2}
      bilingualMode={false}
      bilingualDirection="en-ar"
      translateTargetLang="en"
      activeCurrency={data.currency}
      currencyDisplayMode="symbol"
      computeTableTotals={(el: any) => {
        let subtotal = 0;
        const lineBases: { base: number; taxRate: number }[] = [];

        (el.rows || []).forEach((r: any) => {
          const qty = parseFloat(r.values?.qty) || 0;
          const rate = parseFloat(r.values?.rate) || 0;
          const lineTotal = qty * rate;
          const taxRate = parseFloat(r.values?.taxRate ?? r.values?.tax_rate ?? 0) || 0;
          const inclusive = !!(r.values?.taxInclusive ?? r.values?.tax_inclusive);
          // Proper tax-inclusive pre-tax base calculation (e.g. 280 / 1.05 = 266.67)
          const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
          lineBases.push({ base, taxRate });
          subtotal += base;
        });

        const discount = (el.discounts || []).reduce((s: number, d: any) => s + subtotal * (d.rate / 100), 0);
        const discountRatio = subtotal > 0 ? Math.min(1, discount / subtotal) : 0;
        const productTax = lineBases.reduce((s, lb) => s + (lb.base * (1 - discountRatio)) * (lb.taxRate / 100), 0);
        const taxableValue = subtotal - discount;
        const tax = (el.taxes || []).reduce((s: number, t: any) => s + taxableValue * (t.rate / 100), 0);
        const shipping = el.shippingCost || 0;

        return {
          subtotal,
          tax,
          discount,
          shipping,
          productTax,
          taxBreakdown: savedTaxBreakdown || el.taxBreakdown || [],
          total: data.amount || (taxableValue + productTax + tax + shipping),
        };
      }}
      formatSerialNumber={(s: any) => s ? `${s.prefix}${String(s.current).padStart(s.digits,'0')}${s.suffix}` : ''}
      canvasRef={(node: HTMLDivElement | null) => {
        canvasWorkspaceRef.current = node;
        if (node) {
          const tableNode = node.querySelector('[data-elid]') &&
            Array.from(node.querySelectorAll('[data-elid]')).find(
              (n) => filteredElements.find((e: any) => e.id === n.getAttribute('data-elid'))?.type === 'table'
            ) as HTMLElement | undefined;
          if (tableNode) measureTableRef(tableNode);
        }
      }}
      handleMouseDown={() => {}}
      handleDeleteElement={() => {}}
      handleInlineEditStart={() => {}}
      handleInlineEditSave={() => {}}
      commitCurrentState={() => {}}
      updateElementProps={() => {}}
      handleCanvasMouseDown={() => {}}
      handleCanvasMouseMove={() => {}}
      handleCanvasMouseUp={() => {}}
      onBackgroundClick={() => {}}
    />
    </div>
    </div>
  );
})()}
      </div>
    <div style={{ marginTop: 24, textAlign: 'center', paddingBottom: 16 }}>
        <a href="https://cloudairambo.com" target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 20, padding: '6px 14px', textDecoration: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <span style={{ fontSize: 11, color: '#111827', fontWeight: 500 }}>Powered by</span>
          <span style={{ fontSize: 12, fontWeight: 800, background: 'linear-gradient(135deg,#3b82f6,#9333ea)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>CLOUDAIRAMBO</span>
          <span style={{ fontSize: 10 }}>⚡</span>
        </a>
      </div>
    </div>
  );
}
